/* eslint-disable react-hooks/refs */
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  defaultIfEmpty,
  filter,
  fromEvent,
  last,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import type {
  Group,
  Intersection,
  Object3D,
  Object3DEventMap,
  Vector3Tuple,
} from 'three';
import {
  Color,
  Euler,
  Mesh,
  MeshBasicMaterial,
  Plane,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from 'three';
import { Line2 } from 'three-stdlib';

import { TOOL_CONST } from '@/constant';
import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useEditingLabelBoxStore, useToolMainCameraStore } from '@/store';
import {
  type NEW__LabelingBox,
  subVector,
  withCanvasXY,
  withTarget,
} from '@/util';

import { useToolPcdCanvasContext } from '../ToolPcdCanvas';

const MINIMUM_SIZE_GAP = 0.1;
const EDGE_RAY_LINE_THRESHOLD = 0.1;
const LINE_HOVER_COLOR = 'yellow';

const FSTEditingLabelBox = ({
  box,
  enabled,
  setEnabledBoxEditing,
  visibleTransformControls,
}: {
  box: NEW__LabelingBox;
  enabled: boolean;
  enabledBoxEditing?: boolean;
  setEnabledBoxEditing?: (isEnabled: boolean) => void;
  visibleTransformControls?: boolean;
}) => {
  const { moveBoxList, resizeBoxList, updateQuaternion } =
    useLabelingBoxContext();
  const { viewMode, isViewMode } = useToolPcdCanvasContext();
  const { camera, controls } = useThree();
  // NOTE: parent로써 전체적인 world position 등의 계산을 쉽게 하기 위한 ref. 이 컴포넌트의 종합적인 위치와 회전에 대한 정보를 담음
  const groupRef = useRef<Group>(null);
  // NOTE: useSubscriptions dependency에서 box 객체 변경으로 인한 재구독 방지를 위한 ref
  const boxRef = useRef(box);
  const editingBoxSize = box.dimension;
  const editingBoxPos = box.boxMesh.position;
  const editingBoxQuaternion = box.boxMesh.quaternion;
  const { getMode } = useToolMainCameraStore();

  const {
    isResizing,
    isMoving,
    isRotating,
    sizeOffset,
    moveOffset,
    rotationX,
    rotationY,
    rotationZ,
    setIsResizing,
    setIsMoving,
    setIsRotating,
    setSizeOffset,
    setMoveOffset,
    setRotationX,
    setRotationY,
    setRotationZ,
    updateTransformedBoxData,
    setIsTransforming,
    clear,
  } = useEditingLabelBoxStore();

  const editingBoxGeometryRef = useRef(box.boxGeometry.clone());
  const editingBoxMaterialRef = useRef(box.boxMaterial.clone());

  const editingBoxLineMaterialRef = useRef(box.lineMaterial.clone());
  const editingBoxLineGeometryRef = useRef(box.lineGeometry.clone());
  const editingBoxMeshRef = useRef(
    new Mesh(editingBoxGeometryRef.current, editingBoxMaterialRef.current),
  );
  const editingBoxLine2Ref = useRef(
    new Line2(
      editingBoxLineGeometryRef.current,
      editingBoxLineMaterialRef.current,
    ),
  );

  const sizeControlPointsRef = useRef<Mesh[]>([]);
  const sizeControlPointRadius = (() => {
    const factor = 0.03;

    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP) {
      return Math.max(editingBoxSize[0], editingBoxSize[1]) * factor;
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE) {
      return Math.max(editingBoxSize[0], editingBoxSize[2]) * factor;
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT) {
      return Math.max(editingBoxSize[1], editingBoxSize[2]) * factor;
    }

    return 0.1;
  })();
  const sizeControlPointGeometry = new SphereGeometry(
    sizeControlPointRadius,
    8,
    8,
  );

  const sizeControlPointMaterial = new MeshBasicMaterial({
    color: new Color(0xffffff),
  });

  const rotationControlPointRadius = (() => {
    const factor = 0.03;

    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP) {
      return Math.max(editingBoxSize[0], editingBoxSize[1]) * factor;
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE) {
      return Math.max(editingBoxSize[0], editingBoxSize[2]) * factor;
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT) {
      return Math.max(editingBoxSize[1], editingBoxSize[2]) * factor;
    }

    return 0.1;
  })();
  const rotationControlPointGeometry = new SphereGeometry(
    rotationControlPointRadius,
    8,
    8,
  );
  const roationControlPointMaterial = new MeshBasicMaterial({
    color: new Color(0xffffff),
  });
  const rotationControlPoint = new Mesh(
    rotationControlPointGeometry,
    roationControlPointMaterial,
  );

  const rotationControlPointRef = useRef<Mesh>(null);

  const initVertexOffsets = () => {
    const halfX = editingBoxSize[0] / 2;
    const halfY = editingBoxSize[1] / 2;
    const halfZ = editingBoxSize[2] / 2;

    return [
      new Vector3(-halfX, -halfY, -halfZ),
      new Vector3(-halfX, -halfY, halfZ),
      new Vector3(-halfX, halfY, -halfZ),
      new Vector3(-halfX, halfY, halfZ),
      new Vector3(halfX, -halfY, -halfZ),
      new Vector3(halfX, -halfY, halfZ),
      new Vector3(halfX, halfY, -halfZ),
      new Vector3(halfX, halfY, halfZ),
    ];
  };

  const rotationControlPointPosVector = (() => {
    const factor = 1.4;
    const halfY = editingBoxSize[1] / 2;
    const halfZ = editingBoxSize[2] / 2;
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP) {
      return new Vector3(0, halfY * factor, 0);
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE) {
      return new Vector3(0, 0, halfZ * factor);
    }
    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT) {
      return new Vector3(0, 0, halfZ * factor);
    }

    return new Vector3(0, 0, 0);
  })();

  const setScale = (newDimension: Vector3Tuple) => {
    editingBoxMeshRef.current.scale.set(
      newDimension[0],
      newDimension[1],
      newDimension[2],
    );
    editingBoxLine2Ref.current.scale.set(
      newDimension[0],
      newDimension[1],
      newDimension[2],
    );
  };

  rotationControlPoint.position.copy(
    new Vector3().add(rotationControlPointPosVector),
  );

  // NOTE: RESIZE POINT
  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => !isViewMode.default),
          filter(() => enabled),
          withTarget,
          withCanvasXY,
          filter(
            ({ target }) => !!target.closest(`[data-view-text="${viewMode}"]`),
          ),
          map(e => {
            const raycaster = new Raycaster();
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);
            const intersections = raycaster.intersectObjects(
              sizeControlPointsRef.current,
            );
            return { event: e, raycaster, intersections };
          }),
          filter(({ intersections }) => !!intersections.length),
          map(props => {
            const { intersections, event } = props;
            const startPoint = intersections[0].point.clone();

            const planeNormal = camera
              .getWorldDirection(new Vector3())
              .clone()
              .negate();
            const plane = new Plane();
            plane.setFromNormalAndCoplanarPoint(planeNormal, startPoint);

            const mouse = new Vector2(event.canvasX, event.canvasY);
            const raycaster = new Raycaster();
            raycaster.setFromCamera(mouse, camera);

            const startMousePosition = new Vector3();
            raycaster.ray.intersectPlane(plane, startMousePosition);

            startMousePosition.applyQuaternion(
              boxRef.current.boxMesh.quaternion.clone().invert(),
            );

            return {
              plane,
              startMousePosition,
              ...props,
            };
          }),
          switchMap(({ plane, intersections, startMousePosition }) => {
            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              withTarget,
              filter(
                ({ target }) =>
                  !!target.closest(`[data-view-text="${viewMode}"]`),
              ),
              withCanvasXY,
              map(event => {
                setIsTransforming(true);
                setIsResizing(true);
                const mouse = new Vector2(event.canvasX, event.canvasY);
                const raycaster = new Raycaster();
                raycaster.setFromCamera(mouse, camera);

                const mousePosition = new Vector3();
                raycaster.ray.intersectPlane(plane, mousePosition);
                const displacement = subVector(
                  mousePosition,
                  intersections[0].point,
                );

                const localDisplacement = displacement;

                const localPoint = boxRef.current.boxMesh.position
                  .clone()
                  .applyQuaternion(
                    boxRef.current.boxMesh.quaternion.clone().invert(),
                  );

                const isRightPoint = (() => {
                  return viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                    ? startMousePosition.x > localPoint.x
                    : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                      ? startMousePosition.x > localPoint.x
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                        ? startMousePosition.y < localPoint.y
                        : true;
                })();
                const isUpPoint = (() => {
                  return viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                    ? startMousePosition.y > localPoint.y
                    : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                      ? startMousePosition.z > localPoint.z
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                        ? startMousePosition.z > localPoint.z
                        : true;
                })();

                const isXIncreaseOrDecrease = (() => {
                  if (isRightPoint) {
                    return localDisplacement.x >= 0 ? 1 : -1;
                  } else {
                    return localDisplacement.x >= 0 ? 1 : -1;
                  }
                })();
                const isYIncreaseOrDecrease = (() => {
                  if (isUpPoint) {
                    return localDisplacement.y >= 0 ? 1 : -1;
                  } else {
                    return localDisplacement.y <= 0 ? 1 : -1;
                  }
                })();

                return {
                  event,
                  isRightPoint,
                  isUpPoint,
                  isXIncreaseOrDecrease,
                  isYIncreaseOrDecrease,
                };
              }),
              map(
                ({
                  event,
                  isXIncreaseOrDecrease,
                  isYIncreaseOrDecrease,
                  isRightPoint,
                  isUpPoint,
                }) => {
                  const mouse = new Vector2(event.canvasX, event.canvasY);
                  const raycaster = new Raycaster();
                  raycaster.setFromCamera(mouse, camera);

                  const mousePosition = new Vector3();
                  raycaster.ray.intersectPlane(plane, mousePosition);

                  const worldPosition = intersections[0].point;

                  const displacement = subVector(mousePosition, worldPosition);

                  const forward = new Vector3();
                  camera.getWorldDirection(forward).normalize();

                  const right = new Vector3();
                  right.crossVectors(forward, camera.up);

                  right.applyQuaternion(boxRef.current.boxMesh.quaternion);
                  right.projectOnPlane(forward);
                  right.normalize();

                  const cameraUp = new Vector3();
                  cameraUp.crossVectors(right, forward).normalize();

                  const currentPosition = boxRef.current.boxMesh.position;

                  const factor =
                    viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                      ? camera.position.z >= currentPosition.z
                        ? 1
                        : -1
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                        ? camera.position.y <= currentPosition.y
                          ? 1
                          : -1
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                          ? camera.position.x >= currentPosition.x
                            ? 1
                            : -1
                          : 1;

                  const distanceRight = displacement.dot(right) * factor;
                  const distanceUp = displacement.dot(cameraUp) * factor;

                  return {
                    event,
                    isXIncreaseOrDecrease,
                    isYIncreaseOrDecrease,
                    distanceRight,
                    distanceUp,
                    isRightPoint,
                    isUpPoint,
                  };
                },
              ),
              map(({ distanceRight, distanceUp, isRightPoint, isUpPoint }) => {
                const resize =
                  viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                    ? [distanceRight, distanceUp, 0]
                    : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                      ? [distanceRight, 0, distanceUp]
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                        ? [0, distanceRight, distanceUp]
                        : [1, 1, 1];

                const factors =
                  viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                    ? [isRightPoint ? 1 : -1, isUpPoint ? 1 : -1, 0]
                    : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                      ? [isRightPoint ? 1 : -1, 0, isUpPoint ? 1 : -1]
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                        ? [0, isRightPoint ? -1 : 1, isUpPoint ? 1 : -1]
                        : [1, 1, 1];

                const resizeOffset = [
                  resize[0] * factors[0],
                  resize[1] * factors[1],
                  resize[2] * factors[2],
                ];

                const currentDimension = boxRef.current.dimension;

                if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP) {
                  resizeOffset[0] = Math.max(
                    -currentDimension[0] + MINIMUM_SIZE_GAP,
                    resizeOffset[0],
                  );
                  resizeOffset[1] = Math.max(
                    -currentDimension[1] + MINIMUM_SIZE_GAP,
                    resizeOffset[1],
                  );
                }

                if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE) {
                  resizeOffset[0] = Math.max(
                    -currentDimension[0] + MINIMUM_SIZE_GAP,
                    resizeOffset[0],
                  );
                  resizeOffset[2] = Math.max(
                    -currentDimension[2] + MINIMUM_SIZE_GAP,
                    resizeOffset[2],
                  );
                }

                if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT) {
                  resizeOffset[1] = Math.max(
                    -currentDimension[1] + MINIMUM_SIZE_GAP,
                    resizeOffset[1],
                  );
                  resizeOffset[2] = Math.max(
                    -currentDimension[2] + MINIMUM_SIZE_GAP,
                    resizeOffset[2],
                  );
                }

                const moveOffset = [
                  (resizeOffset[0] / 2) * factors[0],
                  (resizeOffset[1] / 2) * factors[1],
                  (resizeOffset[2] / 2) * factors[2],
                ];

                const moveOffsetVector = new Vector3(
                  ...moveOffset,
                ).applyQuaternion(boxRef.current.boxMesh.quaternion);

                setSizeOffset(resizeOffset);
                setMoveOffset(moveOffsetVector.toArray());

                updateTransformedBoxData({
                  dimension: [
                    boxRef.current.dimension[0] + resizeOffset[0],
                    boxRef.current.dimension[1] + resizeOffset[1],
                    boxRef.current.dimension[2] + resizeOffset[2],
                  ],
                  position: [
                    boxRef.current.position[0] + moveOffset[0],
                    boxRef.current.position[1] + moveOffset[1],
                    boxRef.current.position[2] + moveOffset[2],
                  ],
                  quaternion: groupRef.current!.quaternion.toArray(),
                });

                return {
                  resizeOffset,
                  moveOffsetVector,
                };
              }),
              takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
              defaultIfEmpty(null),
              last(),
              tap(props => {
                if (!props) return;

                const { resizeOffset, moveOffsetVector } = props;
                const moveOffset = moveOffsetVector.toArray();
                const newDimension = [
                  boxRef.current.dimension[0] + resizeOffset[0],
                  boxRef.current.dimension[1] + resizeOffset[1],
                  boxRef.current.dimension[2] + resizeOffset[2],
                ] as Vector3Tuple;

                resizeBoxList([[boxRef.current.id, newDimension]]);
                moveBoxList([
                  [
                    boxRef.current.id,
                    [
                      boxRef.current.position[0] + moveOffset[0],
                      boxRef.current.position[1] + moveOffset[1],
                      boxRef.current.position[2] + moveOffset[2],
                    ],
                  ],
                ]);

                clear();
                setScale(newDimension);
              }),
            );
          }),
        )
        .subscribe(),
    ],
    [enabled],
  );

  // NOTE: RESIZE SIDE
  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => !isViewMode.default),
          filter(() => enabled),
          withTarget,
          withCanvasXY,
          filter(
            ({ target }) => !!target.closest(`[data-view-text="${viewMode}"]`),
          ),
          map(e => {
            const raycaster = new Raycaster();
            raycaster.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const intersections = raycaster.intersectObject(
              editingBoxLine2Ref.current,
              false,
            );

            const resizeRay = new Raycaster();
            resizeRay.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);
            const resizeIntersections = resizeRay.intersectObjects(
              sizeControlPointsRef.current,
            );

            return { event: e, intersections, resizeIntersections };
          }),
          filter(
            ({ intersections, resizeIntersections }) =>
              !!intersections.length && !resizeIntersections.length,
          ),
          map(({ event, intersections }) => ({
            event,
            targetLine: intersections[0],
          })),
          map(({ event, targetLine }) => {
            const startPoint = targetLine.point.clone();
            const planeNormal = camera
              .getWorldDirection(new Vector3())
              .clone()
              .negate();
            const plane = new Plane();
            plane.setFromNormalAndCoplanarPoint(planeNormal, startPoint);
            const mouse = new Vector2(event.canvasX, event.canvasY);
            const raycaster = new Raycaster();
            raycaster.setFromCamera(mouse, camera);

            const startMousePosition = new Vector3();
            raycaster.ray.intersectPlane(plane, startMousePosition);
            startMousePosition.applyQuaternion(
              boxRef.current.boxMesh.quaternion.clone().invert(),
            );

            boxRef.current.setLineColorByIndex(
              targetLine.faceIndex!,
              boxRef.current.color!,
            );

            return {
              plane,
              startMousePosition,
              targetLine,
            };
          }),
          switchMap(
            ({
              plane,
              targetLine,
              startMousePosition,
            }: {
              plane: Plane;
              targetLine: Intersection<Object3D<Object3DEventMap>>;
              startMousePosition: Vector3;
            }) => {
              return fromEvent<MouseEvent>(window, 'mousemove').pipe(
                withTarget,
                filter(
                  ({ target }) =>
                    !!target.closest(`[data-view-text="${viewMode}"]`),
                ),
                withCanvasXY,
                tap(() => {
                  if (controls) {
                    controls.enablePan = false;
                    controls.update();
                  }
                  setEnabledBoxEditing?.(true);
                }),
                map(event => {
                  setIsResizing(true);
                  setIsTransforming(true);
                  const mouse = new Vector2(event.canvasX, event.canvasY);
                  const raycaster = new Raycaster();
                  raycaster.setFromCamera(mouse, camera);

                  const mousePosition = new Vector3();
                  raycaster.ray.intersectPlane(plane, mousePosition);
                  const displacement = subVector(
                    mousePosition,
                    targetLine.point,
                  );

                  const localDisplacement = displacement;
                  const currentPosition = boxRef.current.boxMesh.position;
                  const localPoint = currentPosition
                    .clone()
                    .applyQuaternion(
                      boxRef.current.boxMesh.quaternion.clone().invert(),
                    );

                  const isRightPoint = (() => {
                    return viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                      ? startMousePosition.x > localPoint.x
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                        ? startMousePosition.x > localPoint.x
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                          ? startMousePosition.y < localPoint.y
                          : true;
                  })();
                  const isUpPoint = (() => {
                    return viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                      ? startMousePosition.y > localPoint.y
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                        ? startMousePosition.z > localPoint.z
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                          ? startMousePosition.z > localPoint.z
                          : true;
                  })();

                  const isXLayer = (() => {
                    const currentDimension = boxRef.current.dimension;
                    const xDir = isRightPoint ? 1 : -1;
                    const yDir = isUpPoint ? 1 : -1;

                    return viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                      ? Math.abs(
                          localPoint.x +
                            (currentDimension[0] / 2) * xDir -
                            startMousePosition.x,
                        ) <
                          Math.abs(
                            localPoint.y +
                              (currentDimension[1] / 2) * yDir -
                              startMousePosition.y,
                          )
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                        ? Math.abs(
                            localPoint.x +
                              (currentDimension[0] / 2) * xDir -
                              startMousePosition.x,
                          ) <
                          Math.abs(
                            localPoint.z +
                              (currentDimension[2] / 2) * yDir -
                              startMousePosition.z,
                          )
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                          ? Math.abs(
                              localPoint.y +
                                (currentDimension[1] / 2) * xDir * -1 -
                                startMousePosition.y,
                            ) <
                            Math.abs(
                              localPoint.z +
                                (currentDimension[2] / 2) * yDir -
                                startMousePosition.z,
                            )
                          : true;
                  })();

                  const isXIncreaseOrDecrease = (() => {
                    if (isRightPoint) {
                      return localDisplacement.x >= 0 ? 1 : -1;
                    } else {
                      return localDisplacement.x >= 0 ? 1 : -1;
                    }
                  })();
                  const isYIncreaseOrDecrease = (() => {
                    if (isUpPoint) {
                      return localDisplacement.y >= 0 ? 1 : -1;
                    } else {
                      return localDisplacement.y <= 0 ? 1 : -1;
                    }
                  })();

                  return {
                    event,
                    isRightPoint,
                    isUpPoint,
                    isXIncreaseOrDecrease,
                    isYIncreaseOrDecrease,
                    isXLayer,
                  };
                }),
                map(
                  ({
                    event,
                    isXIncreaseOrDecrease,
                    isYIncreaseOrDecrease,
                    isRightPoint,
                    isUpPoint,
                    isXLayer,
                  }) => {
                    const mouse = new Vector2(event.canvasX, event.canvasY);
                    const raycaster = new Raycaster();
                    raycaster.setFromCamera(mouse, camera);

                    const mousePosition = new Vector3();
                    raycaster.ray.intersectPlane(plane, mousePosition);

                    const worldPosition = targetLine.point;

                    const displacement = subVector(
                      mousePosition,
                      worldPosition,
                    );

                    const forward = new Vector3();
                    camera.getWorldDirection(forward).normalize();

                    const right = new Vector3();
                    right.crossVectors(forward, camera.up);

                    right.applyQuaternion(boxRef.current.boxMesh.quaternion);
                    right.projectOnPlane(forward);
                    right.normalize();

                    const cameraUp = new Vector3();
                    cameraUp.crossVectors(right, forward).normalize();

                    const currentPosition = boxRef.current.boxMesh.position;

                    const factor =
                      viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                        ? camera.position.z >= currentPosition.z
                          ? 1
                          : -1
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                          ? camera.position.y <= currentPosition.y
                            ? 1
                            : -1
                          : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                            ? camera.position.x >= currentPosition.x
                              ? 1
                              : -1
                            : 1;

                    const distanceRight = displacement.dot(right) * factor;
                    const distanceUp = displacement.dot(cameraUp) * factor;

                    return {
                      event,
                      isXIncreaseOrDecrease,
                      isYIncreaseOrDecrease,
                      distanceRight,
                      distanceUp,
                      isRightPoint,
                      isUpPoint,
                      isXLayer,
                    };
                  },
                ),
                map(
                  ({
                    distanceRight,
                    distanceUp,
                    isRightPoint,
                    isUpPoint,
                    isXLayer,
                  }) => {
                    const resize =
                      viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                        ? [
                            isXLayer ? distanceRight : 0,
                            isXLayer ? 0 : distanceUp,
                            0,
                          ]
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                          ? [
                              isXLayer ? distanceRight : 0,
                              0,
                              isXLayer ? 0 : distanceUp,
                            ]
                          : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                            ? [
                                0,
                                isXLayer ? distanceRight : 0,
                                isXLayer ? 0 : distanceUp,
                              ]
                            : [1, 1, 1];

                    const factors =
                      viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                        ? [isRightPoint ? 1 : -1, isUpPoint ? 1 : -1, 0]
                        : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                          ? [isRightPoint ? 1 : -1, 0, isUpPoint ? 1 : -1]
                          : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                            ? [0, isRightPoint ? -1 : 1, isUpPoint ? 1 : -1]
                            : [1, 1, 1];

                    const resizeOffset = [
                      resize[0] * factors[0],
                      resize[1] * factors[1],
                      resize[2] * factors[2],
                    ];

                    const currentDimension = boxRef.current.dimension;

                    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP) {
                      resizeOffset[0] = Math.max(
                        -currentDimension[0] + MINIMUM_SIZE_GAP,
                        resizeOffset[0],
                      );
                      resizeOffset[1] = Math.max(
                        -currentDimension[1] + MINIMUM_SIZE_GAP,
                        resizeOffset[1],
                      );
                    }

                    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE) {
                      resizeOffset[0] = Math.max(
                        -currentDimension[0] + MINIMUM_SIZE_GAP,
                        resizeOffset[0],
                      );
                      resizeOffset[2] = Math.max(
                        -currentDimension[2] + MINIMUM_SIZE_GAP,
                        resizeOffset[2],
                      );
                    }

                    if (viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT) {
                      resizeOffset[1] = Math.max(
                        -currentDimension[1] + MINIMUM_SIZE_GAP,
                        resizeOffset[1],
                      );
                      resizeOffset[2] = Math.max(
                        -currentDimension[2] + MINIMUM_SIZE_GAP,
                        resizeOffset[2],
                      );
                    }

                    const moveOffset = [
                      (resizeOffset[0] / 2) * factors[0],
                      (resizeOffset[1] / 2) * factors[1],
                      (resizeOffset[2] / 2) * factors[2],
                    ];

                    const moveOffsetVector = new Vector3(
                      ...moveOffset,
                    ).applyQuaternion(boxRef.current.boxMesh.quaternion);

                    updateTransformedBoxData({
                      dimension: [
                        boxRef.current.dimension[0] + resizeOffset[0],
                        boxRef.current.dimension[1] + resizeOffset[1],
                        boxRef.current.dimension[2] + resizeOffset[2],
                      ],
                      position: [
                        boxRef.current.position[0] + moveOffset[0],
                        boxRef.current.position[1] + moveOffset[1],
                        boxRef.current.position[2] + moveOffset[2],
                      ],
                      quaternion: groupRef.current!.quaternion.toArray(),
                    });

                    setSizeOffset(resizeOffset);
                    setMoveOffset(moveOffsetVector.toArray());

                    return {
                      resizeOffset,
                      moveOffsetVector,
                    };
                  },
                ),
                takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
                tap(() => {
                  if (controls) {
                    controls.enablePan = true;
                    controls.update();
                  }
                }),
                defaultIfEmpty(null),
                last(),
                tap(props => {
                  if (!props) return;

                  const { resizeOffset, moveOffsetVector } = props;
                  const moveOffset = moveOffsetVector.toArray();
                  const newDimension = [
                    boxRef.current.dimension[0] + resizeOffset[0],
                    boxRef.current.dimension[1] + resizeOffset[1],
                    boxRef.current.dimension[2] + resizeOffset[2],
                  ] as Vector3Tuple;

                  resizeBoxList([[boxRef.current.id, newDimension]]);
                  moveBoxList([
                    [
                      boxRef.current.id,
                      [
                        boxRef.current.position[0] + moveOffset[0],
                        boxRef.current.position[1] + moveOffset[1],
                        boxRef.current.position[2] + moveOffset[2],
                      ],
                    ],
                  ]);

                  clear();
                  setEnabledBoxEditing?.(false);

                  setScale(newDimension);
                }),
              );
            },
          ),
        )
        .subscribe(),
    ],
    [enabled, controls, isViewMode],
  );

  // NOTE: MOVE
  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => !isViewMode.default),
          filter(() => enabled),
          filter(() => !visibleTransformControls),
          withTarget,
          withCanvasXY,
          filter(
            ({ target }) => !!target.closest(`[data-view-text="${viewMode}"]`),
          ),
          map(e => {
            const raycaster = new Raycaster();
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const intersections = raycaster.intersectObject(
              editingBoxMeshRef.current!,
            );

            const edgeRay = new Raycaster();
            edgeRay.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            edgeRay.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const edgeIntersections = edgeRay.intersectObject(
              editingBoxLine2Ref.current,
              false,
            );

            return { event: e, raycaster, intersections, edgeIntersections };
          }),
          filter(
            ({ intersections, edgeIntersections }) =>
              !!intersections.length && !edgeIntersections.length,
          ),
          tap(() => {
            if (controls) {
              controls.enablePan = false;
              controls.update();
            }
            setEnabledBoxEditing?.(true);
          }),
          map(props => {
            const { intersections, event } = props;
            const startPoint = intersections[0].point.clone();
            const planeNormal = camera
              .getWorldDirection(new Vector3())
              .clone()
              .negate();
            const plane = new Plane();
            plane.setFromNormalAndCoplanarPoint(planeNormal, startPoint);

            const mouse = new Vector2(event.canvasX, event.canvasY);
            const raycaster = new Raycaster();
            raycaster.setFromCamera(mouse, camera);

            const startMousePosition = new Vector3();
            raycaster.ray.intersectPlane(plane, startMousePosition);

            startMousePosition.applyQuaternion(
              boxRef.current.boxMesh.quaternion.clone().invert(),
            );

            return {
              plane,
              startMousePosition,
              ...props,
            };
          }),
          switchMap(({ plane, startMousePosition }) => {
            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              withTarget,
              filter(
                ({ target }) =>
                  !!target.closest(`[data-view-text="${viewMode}"]`),
              ),
              withCanvasXY,
              tap(() => {
                setIsMoving(true);
                setIsTransforming(true);
              }),
              map(event => {
                const mouse = new Vector2(event.canvasX, event.canvasY);
                const raycaster = new Raycaster();

                raycaster.setFromCamera(mouse, camera);

                const mouseMovePosition = new Vector3();

                raycaster.ray.intersectPlane(plane, mouseMovePosition);

                mouseMovePosition.applyQuaternion(
                  boxRef.current.boxMesh.quaternion.clone().invert(),
                );
                const moveOffset = [
                  mouseMovePosition.x - startMousePosition.x,
                  mouseMovePosition.y - startMousePosition.y,
                  mouseMovePosition.z - startMousePosition.z,
                ];

                const moveOffsetVector = new Vector3(
                  ...moveOffset,
                ).applyQuaternion(boxRef.current.boxMesh.quaternion);

                setMoveOffset(moveOffsetVector.toArray());
                updateTransformedBoxData({
                  position: [
                    boxRef.current.position[0] + moveOffset[0],
                    boxRef.current.position[1] + moveOffset[1],
                    boxRef.current.position[2] + moveOffset[2],
                  ],
                  dimension: boxRef.current.dimension,
                  quaternion: groupRef.current!.quaternion.toArray(),
                });

                return {
                  moveOffsetVector,
                };
              }),
              takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
              tap(() => {
                if (controls) {
                  controls.enablePan = true;
                  controls.update();
                }
              }),
              defaultIfEmpty(null),
              last(),
              tap(props => {
                if (!props) return;

                const { moveOffsetVector } = props;
                const moveOffset = moveOffsetVector.toArray();

                moveBoxList([
                  [
                    boxRef.current.id,
                    [
                      boxRef.current.position[0] + moveOffset[0],
                      boxRef.current.position[1] + moveOffset[1],
                      boxRef.current.position[2] + moveOffset[2],
                    ],
                  ],
                ]);

                setEnabledBoxEditing?.(false);
                clear();
              }),
            );
          }),
        )
        .subscribe(),
    ],
    [enabled, visibleTransformControls],
  );

  // NOTE: ROTATION
  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => !isViewMode.default),
          filter(() => enabled),
          withTarget,
          withCanvasXY,
          filter(
            ({ target }) => !!target.closest(`[data-view-text="${viewMode}"]`),
          ),
          filter(() => !!rotationControlPointRef.current),
          map(e => {
            const raycaster = new Raycaster();
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);
            const intersection = raycaster.intersectObject(
              rotationControlPointRef.current!,
            );

            return {
              intersection,
              event: e,
            };
          }),
          filter(({ intersection }) => !!intersection.length),
          map(props => {
            const sourceAngleRad = Math.atan2(
              props.event.canvasY,
              props.event.canvasX,
            );

            return {
              ...props,
              sourceAngleRad,
            };
          }),
          switchMap(({ sourceAngleRad }) => {
            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              withTarget,
              withCanvasXY,
              filter(() => !!groupRef.current),
              tap(() => {
                setIsRotating(true);
                setIsTransforming(true);
              }),
              map(event => {
                const axis =
                  viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
                    ? 'z'
                    : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
                      ? 'y'
                      : viewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.FRONT
                        ? 'x'
                        : null;
                return {
                  event,
                  axis: axis as 'x' | 'y' | 'z',
                };
              }),
              filter(({ axis }) => !!axis),
              map(({ event, axis }) => {
                const deltaX = event.canvasX;
                const deltay = event.canvasY;
                const angleRad = Math.atan2(deltay, deltaX) - sourceAngleRad;

                return {
                  event,
                  angleRad,
                  axis,
                };
              }),
              tap(({ angleRad, axis }) => {
                const setter =
                  axis === 'z'
                    ? setRotationZ
                    : axis === 'x'
                      ? setRotationX
                      : axis === 'y'
                        ? setRotationY
                        : void 0;

                updateTransformedBoxData({
                  position: boxRef.current.position,
                  dimension: boxRef.current.dimension,
                  quaternion: groupRef.current!.quaternion.toArray(),
                });
                setter && setter(angleRad);
              }),
              takeUntil(fromEvent<MouseEvent>(window, 'mouseup')),
              defaultIfEmpty(null),
              last(),
              tap(props => {
                if (!props) return;

                if (groupRef.current?.quaternion) {
                  updateQuaternion([
                    [boxRef.current.id, groupRef.current.quaternion.toArray()],
                  ]);
                }
                clear();
              }),
            );
          }),
        )
        .subscribe(),
    ],
    [enabled],
  );

  // NOTE: CURSOR
  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousemove')
        .pipe(
          filter(() => !isViewMode.default),
          filter(() => enabled),
          withTarget,
          withCanvasXY,
          filter(
            ({ target }) => !!target.closest(`[data-view-text="${viewMode}"]`),
          ),
          tap(e => {
            const raycaster = new Raycaster();
            raycaster.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            const edgeRay = new Raycaster();
            edgeRay.params.Line.threshold = EDGE_RAY_LINE_THRESHOLD;
            edgeRay.setFromCamera(new Vector2(e.canvasX, e.canvasY), camera);

            // const lineIntersect = edgeRay.intersectObject(boxRef.current.line2);
            const lineIntersect = edgeRay.intersectObject(
              editingBoxLine2Ref.current,
            );

            !isResizing && lineIntersect.at(0)
              ? box.setLineColorByIndex(
                  lineIntersect.at(0)!.faceIndex!,
                  LINE_HOVER_COLOR,
                )
              : box.color
                ? box.updateColor(box.color)
                : void 0;

            const grabIntersectionsList = [
              raycaster.intersectObjects(sizeControlPointsRef.current),
              raycaster.intersectObject(rotationControlPointRef.current!),
              raycaster.intersectObject(editingBoxMeshRef.current),
              lineIntersect,
            ];

            const canvas = e.target?.closest('canvas') as HTMLCanvasElement;
            if (canvas) {
              if (isResizing || isMoving || isRotating) {
                canvas.style.setProperty('cursor', 'grabbing', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else if (
                grabIntersectionsList.some(
                  intersections => !!intersections.length,
                )
              ) {
                canvas.style.setProperty('cursor', 'grab', 'important');
                canvas.setAttribute('data-cursor-priority', 'editing');
              } else {
                canvas.removeAttribute('data-cursor-priority');
              }
            }
          }),
        )
        .subscribe(),
    ],
    [
      box,
      enabled,
      editingBoxLineGeometryRef,
      isResizing,
      isMoving,
      isRotating,
      isViewMode,
      getMode,
    ],
  );

  useFrame(() => {
    if (!boxRef.current) return;

    const width = editingBoxSize[0];
    const height = editingBoxSize[1];
    const depth = editingBoxSize[2];

    const newWidth = width + sizeOffset[0];
    const newHeight = height + sizeOffset[1];
    const newDepth = depth + sizeOffset[2];

    Object.entries(boxRef.current.boxGeometry.attributes).forEach(
      ([key, value]) => {
        const existingAttr = editingBoxGeometryRef.current.getAttribute(key);

        if (existingAttr && existingAttr.array.length === value.array.length) {
          existingAttr.array.set(value.array);
          existingAttr.needsUpdate = true;
        } else {
          editingBoxGeometryRef.current.setAttribute(key, value.clone());
        }
      },
    );

    Object.entries(boxRef.current.lineGeometry.attributes).forEach(
      ([key, value]) => {
        const existingAttr =
          editingBoxLineGeometryRef.current.getAttribute(key);

        if (existingAttr && existingAttr.array.length === value.array.length) {
          existingAttr.array.set(value.array);
          existingAttr.needsUpdate = true;
        } else {
          editingBoxLineGeometryRef.current.setAttribute(key, value.clone());
        }
      },
    );

    editingBoxMeshRef.current.scale.set(newWidth, newHeight, newDepth);
    editingBoxLine2Ref.current.scale.set(newWidth, newHeight, newDepth);

    if (groupRef.current) {
      groupRef.current.position.set(
        editingBoxPos.x + moveOffset[0],
        editingBoxPos.y + moveOffset[1],
        editingBoxPos.z + moveOffset[2],
      );
    }

    if (sizeControlPointsRef.current.length) {
      const halfX = newWidth / 2;
      const halfY = newHeight / 2;
      const halfZ = newDepth / 2;
      const newOffsets = [
        new Vector3(-halfX, -halfY, -halfZ),
        new Vector3(-halfX, -halfY, halfZ),
        new Vector3(-halfX, halfY, -halfZ),
        new Vector3(-halfX, halfY, halfZ),
        new Vector3(halfX, -halfY, -halfZ),
        new Vector3(halfX, -halfY, halfZ),
        new Vector3(halfX, halfY, -halfZ),
        new Vector3(halfX, halfY, halfZ),
      ];

      newOffsets.forEach((offset, idx) => {
        sizeControlPointsRef.current[idx].position.copy(offset);
      });
    }
  });

  useLayoutEffect(() => {
    if (groupRef.current) {
      if (rotationX) {
        groupRef.current.rotation.reorder('YZX');

        const euler = new Euler().setFromQuaternion(
          boxRef.current.boxMesh.quaternion,
          'YZX',
        );
        groupRef.current.rotation.x = euler.x + rotationX;
      }
      if (rotationY) {
        groupRef.current.rotation.reorder('XZY');

        const euler = new Euler().setFromQuaternion(
          boxRef.current.boxMesh.quaternion,
          'XZY',
        );
        groupRef.current.rotation.y = euler.y - rotationY;
      }
      if (rotationZ) {
        groupRef.current.rotation.reorder('XYZ');
        const euler = new Euler().setFromQuaternion(
          boxRef.current.boxMesh.quaternion,
          'XYZ',
        );
        groupRef.current.rotation.z = euler.z + rotationZ;
      }
    }
  }, [rotationX, rotationY, rotationZ]);

  useEffect(() => {
    if (!groupRef.current) return;

    groupRef.current.quaternion.copy(box.boxMesh.quaternion);

    boxRef.current = box;

    sizeControlPointsRef.current = [];

    initVertexOffsets().forEach(offset => {
      const cp = new Mesh(
        sizeControlPointGeometry.clone(),
        sizeControlPointMaterial.clone(),
      );

      cp.position.copy(new Vector3().add(offset));
      sizeControlPointsRef.current.push(cp);
    });

    if (rotationControlPointRef.current) {
      // 위치 업데이트
      rotationControlPointRef.current.position.copy(
        new Vector3().add(rotationControlPointPosVector),
      );
    }
  }, [box.color, box.dimension, box.position, box.quaternion]);

  return (
    <group
      ref={groupRef}
      position={editingBoxPos}
      quaternion={editingBoxQuaternion}
    >
      <primitive object={editingBoxMeshRef.current} />
      <primitive object={editingBoxLine2Ref.current} />
      {viewMode !== TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT &&
        sizeControlPointsRef.current.map((cp, idx) => (
          <primitive key={`cp-${idx}`} object={cp} />
        ))}
      {viewMode !== TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT && !isResizing && (
        <primitive
          ref={rotationControlPointRef}
          object={rotationControlPoint}
        />
      )}
    </group>
  );
};
export default FSTEditingLabelBox;
